/**
 * AppModal — wrapper Modal cross-platform.
 *
 * Native (iOS/Android) : passthrough vers React Native Modal (full viewport).
 * Web                  : overlay absolument positionné DANS le cadre WebShell.
 *                        - La sidebar reste visible
 *                        - Backdrop flouté + assombri (uniquement la zone contenu)
 *                        - Click sur backdrop → onRequestClose()
 *                        - Touche ESC → onRequestClose()
 *                        - Contenu **scrollable** verticalement si trop long
 *                        - Card centré, max-width 720, max-height ~90%
 *
 * API identique à RN Modal pour un swap drop-in :
 *   <AppModal visible={...} transparent animationType="fade" onRequestClose={...}>
 *     {children}
 *   </AppModal>
 *
 * v1.12.118 — popups conservent la frame mytenue.com sur web (scroll + blur).
 */

import React, { useEffect } from 'react';
import { Modal as RNModal, Platform, View, ScrollView, Pressable, StyleSheet } from 'react-native';
// v1.12.179 — sur web on monte le modal dans un portal (document.body) pour
// l'isoler du re-render du parent. Cela élimine la classe d'erreur "removeChild
// on Node: not a child of this node" qui survenait quand le modal s'ouvrait
// par-dessus une liste en cours de re-render (React 19 + react-native-web).
let createPortal = null;
if (Platform.OS === 'web') {
  try { createPortal = require('react-dom').createPortal; } catch {}
}

export default function AppModal({
  visible,
  transparent = true,
  animationType = 'fade',
  onRequestClose,
  onClose, // alias
  children,
  // Web-specific
  webMaxWidth = 720,
  webBackdropClosable = false, // v1.12.168 — par défaut on NE ferme PAS sur clic outside (UX safe)
  webScrollable = true,
  // v1.13.x — disableHistory : ne PAS pousser d'entrée history (web). À utiliser
  // pour les modals qui ouvrent ensuite un AUTRE AppModal (sinon collision de
  // pile history → popstate en boucle → crash). Le modal se ferme via ses boutons.
  disableHistory = false,
  // Style overrides
  containerStyle,
  backdropStyle,
}) {
  const close = onRequestClose || onClose || (() => {});

  // Sur web, on ferme aussi avec ESC
  useEffect(() => {
    if (Platform.OS !== 'web') return undefined;
    if (!visible) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    if (typeof window !== 'undefined') {
      window.addEventListener('keydown', onKey);
      return () => window.removeEventListener('keydown', onKey);
    }
    return undefined;
  }, [visible, close]);

  // v1.12.568 — Sur web, le bouton "back" du navigateur ferme le modal
  // au lieu de quitter l'app. On pousse une entrée history dummy à l'ouverture
  // puis on écoute popstate. À la fermeture manuelle (ESC, X, click backdrop),
  // on pop l'entrée pour que l'historique reste propre.
  useEffect(() => {
    if (disableHistory) return undefined; // modal qui en ouvre un autre → pas d'history
    if (Platform.OS !== 'web' || !visible) return undefined;
    if (typeof window === 'undefined' || !window.history) return undefined;
    const marker = `appModal_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
    try { window.history.pushState({ appModal: marker }, '', window.location.href); } catch {}
    let closedByPopstate = false;
    const onPop = () => { closedByPopstate = true; close(); };
    window.addEventListener('popstate', onPop);
    return () => {
      window.removeEventListener('popstate', onPop);
      if (!closedByPopstate) {
        try {
          if (window.history.state?.appModal === marker) window.history.back();
        } catch {}
      }
    };
  }, [visible, close]);

  if (!visible) return null;

  if (Platform.OS !== 'web') {
    // Native : Modal RN classique
    return (
      <RNModal
        visible={visible}
        transparent={transparent}
        animationType={animationType}
        onRequestClose={close}
      >
        {children}
      </RNModal>
    );
  }

  // Web : overlay positionné en absolute DANS le cadre parent.
  // La sidebar (rendue à un niveau plus haut dans App.js) reste visible.
  // Le contenu est wrappé dans un ScrollView vertical → scroll OK même si
  // le contenu dépasse la hauteur disponible.
  // v1.12.175 : suppression onStartShouldSetResponder + Pressable interne →
  // ces 2 éléments combinés au responder system de RN-Web créaient des
  // races React DOM (removeChild on Node) lors de l'ouverture rapide d'un
  // modal au-dessus d'un autre composant en cours de re-render.
  const ContentWrapper = webScrollable ? ScrollView : View;
  const contentWrapperProps = webScrollable
    ? {
        contentContainerStyle: s.webScrollContent,
        showsVerticalScrollIndicator: true,
        style: s.webScrollView,
      }
    : { style: s.webContentWrap, pointerEvents: 'box-none' };

  const tree = (
    <View style={[s.webOverlay, containerStyle]} pointerEvents="auto">
      {/* Backdrop flouté + click-to-close (sauf si webBackdropClosable=false) */}
      <Pressable
        style={[s.webBackdrop, backdropStyle]}
        onPress={() => { if (webBackdropClosable) close(); }}
      />
      {/* Contenu : ScrollView pour scroll vertical sur contenu long */}
      <ContentWrapper {...contentWrapperProps}>
        <View style={[s.webContentInner, { maxWidth: webMaxWidth }]}>
          {children}
        </View>
      </ContentWrapper>
    </View>
  );

  // Portal vers document.body : isole le modal du re-render parent et évite
  // les races React 19 / react-native-web sur removeChild.
  if (createPortal && typeof document !== 'undefined' && document.body) {
    return createPortal(tree, document.body);
  }
  return tree;
}

const s = StyleSheet.create({
  webOverlay: {
    // v1.12.119 — position fixed (viewport-relative) + zIndex tres haut
    // pour passer AU-DESSUS des RN Modal (bottom-sheet article, etc.) qui
    // sont rendus en portal au body. Avant : position absolute contraint a
    // WebShell -> les dialogs etaient masques derriere les RN Modal.
    ...(Platform.OS === 'web' ? { position: 'fixed' } : {}),
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 99999,
  },
  webBackdrop: {
    ...(Platform.OS === 'web'
      ? {
          position: 'fixed',
          // Backdrop transparent : juste un léger flou pour ambiance,
          // SANS assombrissement → le contenu MyTenue derrière reste visible.
          backdropFilter: 'blur(3px)',
          WebkitBackdropFilter: 'blur(3px)',
        }
      : {}),
    top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: 'transparent',
  },
  webScrollView: {
    // ScrollView pleine hauteur (overlay parent) → on peut scroll dans le modal
    ...(Platform.OS === 'web'
      ? { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }
      : {}),
    width: '100%',
  },
  webScrollContent: {
    flexGrow: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 24,
    paddingHorizontal: 16,
    minHeight: '100%',
  },
  webContentWrap: {
    width: '100%',
    paddingHorizontal: 16,
    alignItems: 'center',
    justifyContent: 'center',
  },
  webContentInner: {
    width: '100%',
    // v1.12.213 : ombre supprimée (demande UX — pas de shadow derrière l'article)
  },
});
